import React, { useState } from "react";
import { useHistory } from "react-router";
import useApp from "../../store/app/appContext";
import EditIcon from "../../components/Icons/outline/EditIcon";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import SecondaryButton from "../../components/Buttons/SecondaryButton";
import FieldWithEdit from "../../components/FieldItems/FieldWithEdit";
import FieldWithSelectEdit from "../../components/FieldItems/FieldWithSelectEdit";
import useQuery from "../../hooks/useQuery";
import { COLORS } from "../../config";
import { COUNTRIES } from "../../helpers/selectOptions";
import FileInput from "../../components/Inputs/FileInput";
import UpdatePasswordModal from "./UpdatePasswordModal";
import UpdateEmailModal from "./UpdateEmailModal";
import ImageCropModal from "./ImageCropModal";
import UpdateMedicalQuestionnaire from "./UpdateMedicalQuestionnaire";
import useUsers from "../../store/users/usersContext";
import { MoonLoader } from "react-spinners";
import dayjs from "dayjs";

function UserInfo() {
  const {
    appState: { user },
    updateUser,
    updateProfilePicture,
    removeProfilePicture,
    sendVerificationEmail,
    getAllMedicalQuestionnaire,
  } = useApp();

  const { getPatientInfo } = useUsers();

  const history = useHistory();
  const query = useQuery();
  const isMedicalQuestionnaire = query.get('medical-questionnaire');

  const [userEdits, _setUserEdits] = useState();
  const [edit, setEdit] = useState(false);

  const setUserEdits = (newVal) => _setUserEdits({ ...userEdits, ...newVal });

  const [showImageCrop, setShowImageCrop] = useState(false);
  const [removePicture, setRemovePicture] = useState(false);
  const [profilePicture, setProfilePicture] = useState();
  const [medicalQuestionnaire, setMedicalQuestionnaire] = useState();
  const [patientInfo, setPatientInfo] = useState();
  const [isLoading, setIsLoading] = React.useState(false);

  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const [showUpdateEmail, setShowUpdateEmail] = useState(false);
  const [showUpdateMedicalQuestionnaire, setShowUpdateMedicalQuestionnaire] =
    useState(false);

  const handleUpdateClick = () => {
    updateUser(userEdits);
    if (profilePicture) {
      setIsLoading(true)
      updateProfilePicture(profilePicture.file)
      .then(() => setIsLoading(false))
      .catch(()=> setIsLoading(false))
    }
    if (removePicture) removeProfilePicture();
    setEdit(false);
  };

  React.useEffect(() => {
    if (user.user_type === 'patient') {
      getAllMedicalQuestionnaire().then((data) => {
        data = data.sort((a,b) => a.id - b.id)
        setMedicalQuestionnaire(data);
      });
      getPatientInfo().then((data) => {
        setPatientInfo(data);
      });
    }
  }, [user]);

  React.useEffect(()=>{
    if(isMedicalQuestionnaire && medicalQuestionnaire) {
      setShowUpdateMedicalQuestionnaire(true)
    }
  }, [medicalQuestionnaire])

  const handleEditClick = (field) => {
    switch (field) {
      case "personalInfo":
        _setUserEdits({
          first_name: user.first_name,
          last_name: user.last_name,
          date_of_birth: user.date_of_birth
        });
      break;
      case 'contactInfo':
        _setUserEdits({
          location: {
            address: user.location.address,
            postal_code: user.location.postal_code,
            city: user.location.city,
            country: user.location.country,
          },
          mobile_nb: user.mobile_nb
        })
      break;
      case 'medicalInfo':
        setShowUpdateMedicalQuestionnaire(true)
        break;
      default:
        return;
    }
    setProfilePicture(null);
    setRemovePicture(false);
    if (edit === field) setEdit(false);
    if (edit !== field) setEdit(field);
  };

  const countryOptions = [];
  if (!countryOptions.length)
    COUNTRIES.forEach((country) => {
      countryOptions.push({
        value: country,
        label: country,
      });
    });

  const handleImageUpload = (e) => {
    setShowImageCrop(true);
    setProfilePicture({
      url: URL.createObjectURL(e.target.files[0]),
      file: e.target.files[0],
    });
  };

  const handleCancel = () => {
    setProfilePicture(null)
    setShowImageCrop(false)
  }

  const handleRemoveProfilePicture = () => {
    setRemovePicture(true);
  };

  const conceilEmail = (email) => {
    const wordArray = email.split("@");
    const firstWord = wordArray[0];
    const lastWord = wordArray[1];
    let conceiledFirstWord;
    for (var i = 0; i < firstWord.length; i++) {
      if (i === 0) {
        conceiledFirstWord = firstWord[i];
      }
      if (i === firstWord.length - 1) {
        conceiledFirstWord = conceiledFirstWord + "***" + firstWord[i];
      }
    }
    const conceiledEmail = conceiledFirstWord + "@" + lastWord;
    if (conceiledEmail) return conceiledEmail;
    return "";
  };

  const generateVerifiedField = () => {
    if (user.verified) return "Your account is verified";
    if (!user.verified) {
      return (
        <p className="break-normal">
          <span>
            Your account isn't verified. {' '}
          </span>
          <span
            className="underline cursor-pointer"
            onClick={() => sendVerificationEmail(user.email)}
          >
            Click here
          </span>
          {' '} to generate a new activation email.
        </p>
      );
    }
  };

  const handleCloseMedicalQuesionnaire = (value) =>{
    history.replace('/account')
    setShowUpdateMedicalQuestionnaire(value)
  }

  const generateStatusField = () => {
    if (patientInfo && patientInfo.completed)
      return "Your medical questionnaire is up to date";
    if (!patientInfo || !patientInfo.completed) {
      return (
        <p>
          Your medical questionnaire isn't completed.
          <span
            className="underline cursor-pointer mx-1"
            onClick={() => setShowUpdateMedicalQuestionnaire(true)}
          >
            Click here
          </span>
          to complete.
        </p>
      );
    }
  };

  if (!user) return "";

  return (
    <div className="flex flex-col">
      <UpdatePasswordModal
        setShowUpdatePassword={setShowUpdatePassword}
        showUpdatePassword={showUpdatePassword}
      />
      <UpdateEmailModal
        setShowUpdateEmail={setShowUpdateEmail}
        showUpdateEmail={showUpdateEmail}
      />
      <UpdateMedicalQuestionnaire
        setShowUpdateMedicalQuestionnaire={ (val)=>handleCloseMedicalQuesionnaire(val) }
        showUpdateMedicalQuestionnaire={showUpdateMedicalQuestionnaire}
        patientInfo={patientInfo}
      />
       <ImageCropModal
        setShowImageCrop={setShowImageCrop}
        showImageCrop={showImageCrop}
        image={profilePicture}
        setImage={setProfilePicture}
        handleCancel={handleCancel}
      />
      <div className="bg-white rounded shadow py-12 px-10">
        <div className='flex justify-between items-center mb-10'>
          <h1 className=" text-2xl text-secondary">Personal Information</h1>
          <div className={`${edit === 'personalInfo' ? 'rounded-full h-10 w-10 bg-primary flex justify-center items-center' : 'rounded-full h-10 w-10 bg-white flex justify-center items-center'}`}>
              <EditIcon stroke={edit === 'personalInfo' ? 'white' : COLORS.colorSecondary} onClick={() => handleEditClick('personalInfo')} className='cursor-pointer'/>
          </div>
        </div>
        { isLoading ? <div className='flex justify-center align-center right-1 bottom-80 pt-8 w-full'>
            <MoonLoader color={COLORS.colorPrimary}/>
        </div> :
        <div className='grid grid-cols-1 gap-y-4'>  
          <FieldWithEdit label='First name' data={edit === 'personalInfo' ? userEdits.first_name : user.first_name} onChange={(e) => setUserEdits({first_name: e.target.value})} edit={edit === 'personalInfo'}/>
          <FieldWithEdit label='Last name' data={edit === 'personalInfo' ? userEdits.last_name : user.last_name} onChange={(e) => setUserEdits({last_name: e.target.value})} edit={edit === 'personalInfo'}/>
          {user.login_type !== 'email' && 
          <FieldWithEdit locked label='Email' data={conceilEmail(user.email)} edit={edit === 'personalInfo'}/>
          }
          {user.login_type === 'email' && 
          <>
            <FieldWithEdit 
              button 
              buttonText={'Update'} 
              buttonOnClick={() => setShowUpdateEmail(true)}
              label='Email' 
              data={conceilEmail(user.email)} 
              edit={edit === 'personalInfo'}
            />
            <FieldWithEdit 
              button 
              buttonText={'Update'} 
              buttonOnClick={() => setShowUpdatePassword(true)}
              label='Password' 
              data={'***'} 
              edit={edit === 'personalInfo'}
            />
          </>
          }
          <FieldWithEdit override label='Profile picture' edit={edit === 'personalInfo'}>
            <div className="flex items-center mt-1 sm:mt-0 justify-between w-full">
              {(!user.profile_pic_url || removePicture) &&
              <>
                {profilePicture?.url && 
                <div className='h-12 w-12 rounded-full border-2 border-primary overflow-hidden'>
                  <img className='transform scale-100' src={profilePicture?.url || user.profile_pic_url} alt=''/>
                </div>
                }
                {!profilePicture && 
                <span className="h-12 w-12 rounded-full overflow-hidden bg-gray-100">
                  <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </span>
                }
              </>
              }
              {user.profile_pic_url && !removePicture &&
              <div className='flex items-center'>
                <div className='h-12 w-12 rounded-full border-2 border-primary overflow-hidden'>
                  <img className='transform scale-100' src={profilePicture?.url || user.profile_pic_url} alt=''/>
                </div>
                {edit === 'personalInfo' && <SecondaryButton text='Remove' className='ml-2' onClick={handleRemoveProfilePicture} />}
              </div>
              }
              {edit === 'personalInfo' && 
                <FileInput handleFileUpload={handleImageUpload} buttonText='Upload' className='mr-3' accept="image/x-png,image/gif,image/jpeg"/>              
              }
            </div>
          </FieldWithEdit>
          <FieldWithEdit 
            type='date' 
            label='Date of Birth' 
            data={edit === 'personalInfo' ? dayjs(userEdits.date_of_birth).format('YYYY-MM-DD') : dayjs(user.date_of_birth).format('DD/MM/YYYY')} 
            onChange={(e) =>{
              setUserEdits({date_of_birth : e.target.value})
            }} 
            edit={edit === 'personalInfo'}
          />
          <FieldWithEdit data={generateVerifiedField()} label={'Verified'} />
          {edit === 'personalInfo' && 
          <div className='flex flex-row-reverse'>
            <div className='w-32'>
              <PrimaryButton className="mt-8" text='Save' onClick={handleUpdateClick} />
            </div>
          </div>}
        </div>}
      </div>
      
      <div className="bg-white rounded shadow py-12 px-10 mt-6">
        <div className='flex justify-between items-center mb-10'>
          <h1 className="text-2xl text-secondary">Contact details</h1>
          <div className={`${edit === 'contactInfo' ? 'rounded-full h-10 w-10 bg-primary flex justify-center items-center' : 'rounded-full h-10 w-10 bg-white flex justify-center items-center'}`}>
              <EditIcon stroke={edit === 'contactInfo' ? 'white' : COLORS.colorSecondary} onClick={() => handleEditClick('contactInfo')} className='cursor-pointer'/>
          </div>
        </div>
        <div className='grid grid-cols-1 gap-y-4'>  
          <FieldWithSelectEdit label='Country' options={countryOptions} data={edit === 'contactInfo' ? userEdits.location.country : user.location.country} onChange={(e) => setUserEdits({location: {...userEdits.location, country: e.value}})} edit={edit === 'contactInfo'}/> 
          <FieldWithEdit label='City' data={edit === 'contactInfo' ? userEdits.location.city : user.location.city} onChange={(e) => setUserEdits({location: {...userEdits.location, city: e.target.value}})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Address' data={edit === 'contactInfo' ? userEdits.location.address : user.location.address} onChange={(e) => setUserEdits({location: {...userEdits.location, address: e.target.value}})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Postal code' data={edit === 'contactInfo' ? userEdits.location.postal_code : user.location.postal_code} onChange={(e) => setUserEdits({location: {...userEdits.location, postal_code: e.target.value}})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Mobile' data={edit === 'contactInfo' ? userEdits.mobile_nb : user.mobile_nb} onChange={(e) => setUserEdits({mobile_nb: e.target.value})} edit={edit === 'contactInfo'}/>
          {edit === 'contactInfo' && 
          <div className='flex flex-row-reverse'>
            <div className='w-32'>
              <PrimaryButton className="mt-8" text='Save' onClick={handleUpdateClick} />
            </div>
          </div>}
        </div>
      </div>
      {user.user_type === "patient" && (
        <>
          <div className="bg-white rounded shadow py-12 px-10 mt-6">
            <div className="flex justify-between items-center mb-10 ">
              <h1 className="text-2xl text-secondary">
                Medical questionnaire
              </h1>
              <div className={"rounded-full h-10 w-10 bg-white flex justify-center items-center"}>
                <EditIcon
                  stroke={COLORS.colorSecondary}
                  onClick={() => handleEditClick("medicalInfo")}
                  className="cursor-pointer"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-y-4">
              <FieldWithEdit
                label="Status"
                data={generateStatusField()}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default UserInfo;